import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';
import { useFormState } from 'react-final-form';

import {
  AddressRemoveButton,
  AddressStreetFields,
  AddressStateZipFields,
} from './components';

import './AddressField.scss';

/**
 * Builds an updated address location object by merging current address values
 * with a single changed attribute.
 */
const buildUpdatedLocation = (addressValues, attribute, value) => ({
  line_1: addressValues.line_1 || '',
  city: addressValues.city || '',
  state: addressValues.state || '',
  postal_code: addressValues.postal_code || '',
  [attribute]: value,
});

/**
 * Validates the address if a validator function is provided, and updates
 * the invalid-warning state accordingly. Silently ignores validation errors.
 */
const validateAddressField = async ({
  isValidAddress,
  addressValues,
  attribute,
  value,
  setShowInvalidWarning,
}) => {
  if (!isValidAddress) return;
  try {
    const updatedLocation = buildUpdatedLocation(addressValues, attribute, value);
    const isInvalid = await isValidAddress(updatedLocation);
    setShowInvalidWarning(isInvalid);
  } catch {
    // ignore validation errors
  }
};

export function AddressFieldGroup({
  displayFieldSet,
  fieldPrefix,
  id,
  inline,
  inputStyle,
  isValidAddress,
  label,
  labelStyle,
  multi,
  remove,
  usStates,
}) {
  const { values } = useFormState({ subscription: { values: true } });
  const [showRemove, setShowRemove] = useState(false);
  const [showInvalidWarning, setShowInvalidWarning] = useState(false);

  // Get current address values from form state for validation
  const getAddressValues = useCallback(() => {
    const parts = fieldPrefix.split(/[[\].]+/).filter(Boolean);
    let current = values;
    for (const part of parts) {
      current = current?.[part];
    }
    return current || {};
  }, [fieldPrefix, values]);

  const onFieldChange = useCallback(async (attribute, value, inputOnChange) => {
    inputOnChange(value);
    const addressValues = getAddressValues();
    await validateAddressField({
      isValidAddress,
      addressValues,
      attribute,
      value,
      setShowInvalidWarning,
    });
  }, [isValidAddress, getAddressValues]);

  const toggleRemove = useCallback(() => {
    setShowRemove((prev) => !prev);
  }, []);

  const confirmRemove = useCallback(() => {
    if (remove) {
      remove();
    }
    setShowRemove(false);
  }, [remove]);

  const groupLabelClass = () => classNames('group-label', {
    hidden: label === undefined,
    inline,
  });

  const addressId = id || fieldPrefix;

  return (
    <div className="address-field">
      <div className="address-field__title row">
        <div className="col-xs-12">
          <strong className={groupLabelClass()} style={labelStyle}>
            {label}
          </strong>
        </div>
      </div>

      <AddressStreetFields
        addressId={addressId}
        displayFieldSet={displayFieldSet}
        fieldPrefix={fieldPrefix}
        inline={inline}
        inputStyle={inputStyle}
        labelStyle={labelStyle}
        onFieldChange={onFieldChange}
      />

      <AddressStateZipFields
        addressId={addressId}
        displayFieldSet={displayFieldSet}
        fieldPrefix={fieldPrefix}
        inline={inline}
        inputStyle={inputStyle}
        labelStyle={labelStyle}
        onFieldChange={onFieldChange}
        usStates={usStates}
      />

      <div className="row">
        <div className="col-xs-9 address-field__error">
          {showInvalidWarning && <p>*Invalid Address. Please make sure you enter a valid address.</p>}
        </div>
        <div className="col-xs-3">
          <AddressRemoveButton
            confirmRemove={confirmRemove}
            multi={multi}
            remove={remove}
            showRemove={showRemove}
            toggleRemove={toggleRemove}
          />
        </div>
      </div>
    </div>
  );
}

AddressFieldGroup.propTypes = {
  displayFieldSet: PropTypes.string,
  fieldPrefix: PropTypes.string.isRequired,
  id: PropTypes.string,
  inline: PropTypes.bool,
  inputStyle: PropTypes.object,
  isValidAddress: PropTypes.func,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  labelStyle: PropTypes.object,
  multi: PropTypes.bool,
  remove: PropTypes.func,
  usStates: PropTypes.array.isRequired,
};

AddressFieldGroup.defaultProps = {
  displayFieldSet: 'full',
  inline: true,
  inputStyle: {},
  isValidAddress: null,
  labelStyle: {},
  multi: true,
};

function mapStateToProps(state, ownProps) {
  if (!state.session) {
    return {
      addressTypes: _.get(ownProps, 'addressTypes', ownProps.enums.contact_data.address_types),
      usStates: ownProps.enums.geography.us_states,
    };
  }
  return {
    addressTypes: _.get(ownProps, 'addressTypes', state.session.enums.contact_data.address_types),
    usStates: state.session.enums.geography.us_states,
  };
}

export default connect(mapStateToProps)(AddressFieldGroup);
